import { useCallback, useEffect, useId, useState, type ReactNode } from "react"
import { CheckCircle2 } from "lucide-react"
import { cn } from "@/lib/utils"
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogTitle,
} from "../Dialog/Dialog"
import { Button } from "../Button/Button"
import { Input } from "../Input/Input"

export interface ConfirmActionDialogProps {
  open: boolean
  onOpenChange: (open: boolean) => void
  title?: string
  body?: ReactNode
  confirmLabel?: string
  cancelLabel?: string
  successTitle?: string
  successBody?: ReactNode
  successDwellMs?: number
  onConfirm?: () => void | Promise<void>
  destructive?: boolean
  /**
   * Hold Confirm shut until this word is typed — for wipes that cannot be
   * undone, where a mis-aimed click would destroy an hour's work.
   *
   * Matched trimmed and case-insensitively: it is a check that the trader MEANT
   * it, not a spelling test. Opt-in, so every existing caller keeps the plain
   * one-click question.
   */
  typeToConfirm?: string
  /**
   * Skip the "Done" stage and close as soon as the act is carried out. A delete
   * announcing its success is telling the trader what they can already see, and
   * holding the dialog open for it delays the view of what is left.
   */
  noSuccess?: boolean
  className?: string
}

const MOBILE_SHEET_CLASSES =
  "max-sm:top-auto max-sm:bottom-0 max-sm:left-0 max-sm:translate-x-0 max-sm:translate-y-0 max-sm:w-full max-sm:max-w-full max-sm:rounded-b-none max-sm:rounded-t-xl max-sm:max-h-[88dvh] max-sm:pb-[max(1.5rem,env(safe-area-inset-bottom))]"

export function ConfirmActionDialog({
  open,
  onOpenChange,
  title = "Are you sure?",
  body,
  confirmLabel = "Confirm",
  cancelLabel = "Cancel",
  successTitle = "Done",
  successBody,
  successDwellMs = 1400,
  onConfirm,
  destructive = false,
  typeToConfirm,
  noSuccess = false,
  className,
}: ConfirmActionDialogProps) {
  const [stage, setStage] = useState<"confirm" | "success">("confirm")
  const [typed, setTyped] = useState("")
  const typedId = useId()

  useEffect(() => {
    // Reset the typing too: a dialog reopened still holding the word would
    // arrive with its gate already unlocked, which is the one thing the gate
    // exists to prevent.
    if (open) {
      setStage("confirm")
      setTyped("")
    }
  }, [open])

  useEffect(() => {
    if (stage !== "success") return
    const t = setTimeout(() => onOpenChange(false), successDwellMs)
    return () => clearTimeout(t)
  }, [stage, successDwellMs, onOpenChange])

  const gated = !!typeToConfirm
  const matched =
    gated && typed.trim().toLowerCase() === String(typeToConfirm).trim().toLowerCase()

  const handleConfirm = useCallback(async () => {
    try {
      await onConfirm?.()
    } catch (e) {
      console.warn("ConfirmActionDialog onConfirm threw", e)
    }
    if (noSuccess) {
      onOpenChange(false)
      return
    }
    setStage("success")
  }, [onConfirm, noSuccess, onOpenChange])

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        className={cn(
          "v1 bg-card max-w-[460px] w-[94vw] p-6",
          MOBILE_SHEET_CLASSES,
          className,
        )}
      >
        {stage === "success" ? (
          <div className="flex flex-col items-center gap-3 py-4 text-center">
            <CheckCircle2 size={44} strokeWidth={1.5} className="text-success" />
            <DialogTitle className="text-section text-foreground">
              {successTitle}
            </DialogTitle>
            {successBody && (
              <DialogDescription className="text-body text-muted-foreground">
                {successBody}
              </DialogDescription>
            )}
          </div>
        ) : (
          <div className="flex flex-col gap-5">
            <div>
              <DialogTitle className="text-section text-foreground">
                {title}
              </DialogTitle>
              {body && (
                <DialogDescription className="text-body text-muted-foreground mt-1.5">
                  {body}
                </DialogDescription>
              )}
            </div>
            {gated && (
              <div className="flex flex-col gap-1.5">
                <label htmlFor={typedId} className="text-label text-muted-foreground">
                  Type <span className="text-foreground">{typeToConfirm}</span> to confirm
                </label>
                <Input
                  id={typedId}
                  autoFocus
                  value={typed}
                  onChange={(e) => setTyped(e.target.value)}
                  onKeyDown={(e) => {
                    if (e.key === "Enter" && matched) handleConfirm()
                  }}
                  placeholder={typeToConfirm}
                  className="text-body"
                />
              </div>
            )}
            <div className="flex justify-end gap-2 pt-1">
              <Button
                variant="ghost"
                size="sm"
                className="text-label"
                onClick={() => onOpenChange(false)}
              >
                {cancelLabel}
              </Button>
              <Button
                size="sm"
                variant={destructive ? "destructive" : "default"}
                className="text-label"
                disabled={gated && !matched}
                onClick={handleConfirm}
              >
                {confirmLabel}
              </Button>
            </div>
          </div>
        )}
      </DialogContent>
    </Dialog>
  )
}
